import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ClipboardList, Plus, ChevronLeft, ChevronRight, Search, ExternalLink, SearchX, X } from 'lucide-react';
import { getModuleLabel, getStageLabel, type WorkshopStageConfig } from '@mustauto/types';
import { useStageConfig } from '@/hooks/useSettings';
import { Card, CardContent, ErrorState } from '@mustauto/ui/web';
import { EmptyState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import { SkeletonTable } from '@mustauto/ui/web';
import { SidePanel } from '@/components/ui/side-panel';
import { FilterChips, type FilterChip } from '@/components/ui/filter-chips';
import { ExportDropdown } from '@/components/ui/export-dropdown';
import { useServiceOrders, useServiceOrder, type OSFilters, type OSListItem } from '@/hooks/useServiceOrders';
import { useAuth } from '@/hooks/useAuth';
import { useOSRealtime } from '@/hooks/useOSRealtime';
import { STATUS_MAP } from '@/lib/status';
import { formatReais, formatCentavos } from '@/lib/utils';
import { formatApiError, maskPlate } from '@mustauto/utils';
import { useToast } from '@mustauto/ui/web';
import { api } from '@/lib/api';
import { useCan } from '@/hooks/useCan';

interface TeamMember {
  id: string;
  name: string;
}

const STATUS_CHIPS: FilterChip[] = [
  { key: '', label: 'Todas' },
  { key: 'in_analysis', label: 'Em Análise' },
  { key: 'in_execution', label: 'Em Execução' },
  { key: 'waiting_part', label: 'Aguardando Peça' },
  { key: 'outsourced', label: 'Terceirizado' },
  { key: 'completed', label: 'Concluída' },
  { key: 'waiting_payment', label: 'Ag. Pagamento' },
  { key: 'delivered', label: 'Entregue' },
  { key: 'cancelled', label: 'Cancelada' },
];

function OSSidePanelContent({ osId, onNavigate }: { osId: string; onNavigate: () => void }) {
  const navigate = useNavigate();
  const { os, isLoading, updateStatus, isUpdatingStatus } = useServiceOrder(osId);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!os || newStatus === os.status) return;
    await updateStatus({ status: newStatus });
  }, [os, updateStatus]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-32 animate-pulse rounded bg-muted-foreground/10" />
        <div className="h-4 w-48 animate-pulse rounded bg-muted-foreground/10" />
        <div className="h-4 w-40 animate-pulse rounded bg-muted-foreground/10" />
        <div className="mt-6 h-32 w-full animate-pulse rounded bg-muted-foreground/10" />
      </div>
    );
  }

  if (!os) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Ordem de serviço não encontrada.
      </p>
    );
  }

  const statusCfg = STATUS_MAP[os.status];
  const isReadOnly = os.status === 'completed' || os.status === 'cancelled';
  const items = os.items ?? [];
  const totalParts = Number(os.totalParts) || 0;
  const totalLabor = Number(os.totalLabor) || 0;
  const totalOutsourced = Number(os.totalOutsourced) || 0;
  const totalGeral = totalParts + totalLabor + totalOutsourced;

  const STATUS_OPTIONS = [
    'in_analysis',
    'in_execution',
    'waiting_part',
    'outsourced',
    'completed',
    'waiting_payment',
    'delivered',
    'cancelled',
  ] as const;

  return (
    <div className="space-y-5">
      {/* Header: status + OS number */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">OS #{os.number}</h3>
        {statusCfg ? (
          <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
        ) : (
          <Badge variant="outline">{os.status}</Badge>
        )}
      </div>

      {/* Vehicle info */}
      <div className="rounded-lg border p-4 space-y-2">
        <p className="text-xs font-medium uppercase text-muted-foreground">Veículo</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Placa</p>
            <p className="font-mono font-semibold">{os.plate}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Marca / Modelo</p>
            <p className="font-medium">{os.make} {os.model}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Ano</p>
            <p className="font-medium">{os.year}</p>
          </div>
          {os.customerName && (
            <div>
              <p className="text-xs text-muted-foreground">Cliente</p>
              <p className="font-medium">{os.customerName}</p>
            </div>
          )}
        </div>
      </div>

      {/* Mechanic */}
      {os.mechanicName && (
        <div className="rounded-lg border p-4">
          <p className="text-xs font-medium uppercase text-muted-foreground">Mecânico</p>
          <p className="mt-1 font-medium">{os.mechanicName}</p>
        </div>
      )}

      {/* Items */}
      {items.length > 0 && (
        <div className="rounded-lg border p-4 space-y-3">
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Itens ({items.length})
          </p>
          <div className="space-y-2">
            {items.map((item, i) => {
              const typeLabel = item.type === 'part' ? 'Peça' : item.type === 'labor' ? 'M. obra' : 'Terceirizado';
              return (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {typeLabel}
                    </span>
                    <span>{item.description}</span>
                  </div>
                  <span className="font-medium">{formatCentavos(item.total)}</span>
                </div>
              );
            })}
          </div>
          <div className="border-t pt-2 flex justify-between text-sm font-semibold">
            <span>Total</span>
            <span>{formatReais(totalGeral)}</span>
          </div>
        </div>
      )}

      {/* Dates */}
      <div className="rounded-lg border p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Aberta em</span>
          <span>
            {new Date(os.createdAt).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        </div>
        {os.deadline && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Prazo</span>
            <span>{new Date(os.deadline).toLocaleDateString('pt-BR')}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-3 border-t pt-4">
        {/* Status change */}
        {!isReadOnly && (
          <div className="flex items-center gap-3">
            <label className="text-sm text-muted-foreground whitespace-nowrap">Mudar status:</label>
            <select
              className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm"
              value={os.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={isUpdatingStatus}
            >
              {STATUS_OPTIONS.map((s) => {
                const cfg = STATUS_MAP[s];
                return (
                  <option key={s} value={s}>
                    {cfg?.label ?? s}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <button
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          onClick={() => {
            onNavigate();
            navigate({ to: '/service-orders/$osId', params: { osId } });
          }}
        >
          <ExternalLink size={14} />
          Ver detalhes completos
        </button>
      </div>
    </div>
  );
}

/**
 * Filtros que chegam pela URL — os links "Ver OS" do "Requer atenção" do
 * dashboard (`?status=waiting_payment`, `?stale=true`). A lista ignorava a URL,
 * e os dois links abriam a lista inteira. O parser padrão do TanStack Router lê
 * `true` como booleano; o texto também é aceito, para quem digitou a URL.
 */
function filtersFromSearch(search: Record<string, unknown>): OSFilters {
  const filters: OSFilters = { page: 1, limit: 20 };
  const status = search.status;
  if (typeof status === 'string' && STATUS_CHIPS.some((c) => c.key === status && c.key !== '')) {
    filters.status = status;
  }
  if (search.stale === true || search.stale === 'true') filters.stale = true;
  return filters;
}

/** "Funilaria · Cabine de pintura" (com o nome da oficina, F3.8) — nada para OS sem tipo, que é a maioria. */
function moduleStageText(
  module: string | null | undefined,
  stage: string | null | undefined,
  config: WorkshopStageConfig,
): string | null {
  if (!module) return null;
  return stage ? `${getModuleLabel(module)} · ${getStageLabel(stage, config)}` : getModuleLabel(module);
}

export function ServiceOrdersPage() {
  const navigate = useNavigate();
  const stageConfig = useStageConfig();
  const { user, isDemo } = useAuth();
  const can = useCan();
  // Abrir OS (`os.abrir`) e exportar a lista (`clientes.exportar`) — Fase 3.
  const canOpen = !isDemo && can('os.abrir');
  const { toast } = useToast();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const [filters, setFilters] = useState<OSFilters>(() => filtersFromSearch(search));

  // `status` e `stale` moram na URL e no estado, e os dois precisam andar
  // juntos nos dois sentidos:
  //   - estado → URL: sem isto, "Mostrar todas" limpava o filtro na tela e
  //     deixava o `?stale=true` no endereço — recarregar trazia o filtro de
  //     volta;
  //   - URL → estado: a rota é a mesma, então clicar em "Ordens de Serviço" no
  //     menu (ou na paleta de comandos) não remonta a página — sem isto, o
  //     endereço ficava limpo e a tabela seguia só com as paradas.
  // Cada lado só age quando os dois divergem, o que fecha o ciclo. O lado
  // estado → URL lê a URL por ref de propósito: com ela nas dependências, ele
  // rodaria no mesmo commit em que a URL mudou, ainda com o estado velho, e a
  // desfaria.
  const fromUrl = filtersFromSearch(search);
  const urlStatus = fromUrl.status;
  const urlStale = fromUrl.stale === true;
  const urlRef = useRef({ status: urlStatus, stale: urlStale });
  urlRef.current = { status: urlStatus, stale: urlStale };

  useEffect(() => {
    setFilters((prev) =>
      prev.status === urlStatus && (prev.stale === true) === urlStale
        ? prev
        : { ...prev, status: urlStatus, stale: urlStale || undefined, page: 1 },
    );
  }, [urlStatus, urlStale]);

  useEffect(() => {
    const url = urlRef.current;
    if (filters.status === url.status && (filters.stale === true) === url.stale) return;
    // `replace` para não empilhar histórico a cada chip.
    void navigate({
      to: '/service-orders',
      search: {
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.stale ? { stale: true } : {}),
      },
      replace: true,
    });
  }, [filters.status, filters.stale, navigate]);
  const [plateInput, setPlateInput] = useState('');
  const [plateDebounce, setPlateDebounce] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [numberInput, setNumberInput] = useState('');
  const [numberDebounce, setNumberDebounce] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [selectedOsId, setSelectedOsId] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  useOSRealtime(user?.workshopId ?? null);

  const handleExport = useCallback(async (format: 'csv' | 'pdf') => {
    setExportLoading(true);
    try {
      const params: Record<string, string> = { format };
      if (filters.status) params.status = filters.status;
      if (filters.mechanicId) params.mechanicId = filters.mechanicId;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.plate) params.plate = filters.plate;
      if (filters.stale) params.stale = 'true';
      if (filters.number !== undefined) params.number = String(filters.number);

      const res = await api.get('/service-orders/export', {
        params,
        responseType: 'blob',
      });
      const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `os-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // F-UI Phase 5: surface export failure with a toast. Antes era
      // silent catch — usuário via só o loading parar, sem saber se foi
      // sucesso (download não veio) ou erro. Sentry recebe a exception.
      console.error('[ServiceOrders] export failed', err);
      toast({
        type: 'error',
        title: 'Falha ao exportar',
        description: formatApiError(err, 'Não foi possível gerar o arquivo. Tente novamente em instantes.'),
      });
    } finally {
      setExportLoading(false);
    }
  }, [filters, toast]);

  const { data, isLoading, isError, error, refetch } = useServiceOrders(filters);

  const teamQuery = useQuery<{ team: TeamMember[] }>({
    queryKey: ['team-list'],
    queryFn: async () => {
      const res = await api.get('/workshops/my/team');
      return res.data;
    },
  });

  const updateFilter = useCallback((key: keyof OSFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  }, []);

  const handleNumberChange = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    setNumberInput(digits);
    if (numberDebounce) clearTimeout(numberDebounce);
    const timeout = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        number: digits ? Number(digits) : undefined,
        page: 1,
      }));
    }, 400);
    setNumberDebounce(timeout);
  }, [numberDebounce]);

  const handlePlateChange = useCallback((value: string) => {
    // F-UI: maskPlate aplica display "ABC-1D23" durante digitação. Backend
    // já normaliza via plateUtils.normalizePlate, então pode receber
    // partial input (filter LIKE %X%).
    const masked = maskPlate(value);
    setPlateInput(masked);
    if (plateDebounce) clearTimeout(plateDebounce);
    const timeout = setTimeout(() => {
      updateFilter('plate', masked);
    }, 400);
    setPlateDebounce(timeout);
  }, [plateDebounce, updateFilter]);

  // Build chips with counts from the data (count only for selected status)
  const statusChips = useMemo<FilterChip[]>(() => {
    const total = data?.total ?? 0;
    return STATUS_CHIPS.map((chip) => ({
      ...chip,
      count: chip.key === (filters.status ?? '') ? (chip.key === '' ? total : total) : undefined,
    }));
  }, [data?.total, filters.status]);

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  // Filtro ativo = qualquer coisa além da paginação. Sem separar os dois casos,
  // a oficina que nunca abriu uma OS era recebida com "ajuste os filtros" — sem
  // ter filtro nenhum para ajustar, e sem nada dizendo por onde começar.
  const hasActiveFilters =
    !!filters.status ||
    !!filters.mechanicId ||
    !!filters.from ||
    !!filters.to ||
    !!filters.plate ||
    !!filters.stale ||
    filters.number !== undefined;
  // `total` (e não `orders.length`) é a contagem da oficina inteira: numa página
  // vazia além da primeira, "sua primeira OS" seria mentira.
  const neverHadOS = !hasActiveFilters && total === 0;
  const page = data?.page ?? 1;
  const limit = data?.limit ?? 20;
  const totalPages = Math.ceil(total / limit);

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList size={24} />
          <h1 className="text-xl font-semibold">Ordens de Servico</h1>
          {total > 0 && (
            <span className="text-sm text-muted-foreground">({total})</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {can('clientes.exportar') && (
            <ExportDropdown
              onExportCSV={() => handleExport('csv')}
              onExportPDF={() => handleExport('pdf')}
              loading={exportLoading}
            />
          )}
          {canOpen && (
            <button
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              onClick={() => navigate({ to: '/service-orders/new' })}
            >
              <Plus size={16} />
              Nova OS
            </button>
          )}
        </div>
      </div>

      {/* Status filter chips */}
      <FilterChips
        chips={statusChips}
        selected={filters.status ?? ''}
        onSelect={(key) => updateFilter('status', key)}
      />

      {filters.stale && (
        <div className="flex items-center gap-2 text-sm">
          <span className="rounded-full border border-input bg-muted px-3 py-1 font-medium">
            Só OS paradas — sem movimento além do tempo combinado
          </span>
          <button
            type="button"
            className="flex h-8 items-center gap-1 rounded-md px-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setFilters((prev) => ({ ...prev, stale: undefined, page: 1 }))}
            aria-label="Mostrar todas as OS"
          >
            <X size={14} />
            Mostrar todas
          </button>
        </div>
      )}

      {/* Secondary filters */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Nº da OS</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                type="text"
                inputMode="numeric"
                placeholder="123"
                maxLength={9}
                className="h-9 w-28 rounded-md border border-input bg-background pl-8 pr-3 font-mono text-sm"
                value={numberInput}
                onChange={(e) => handleNumberChange(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Placa</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="ABC1D23 ou ABC-1234"
                maxLength={8}
                className="h-9 rounded-md border border-input bg-background pl-8 pr-3 font-mono text-sm uppercase tracking-wider"
                value={plateInput}
                onChange={(e) => handlePlateChange(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Mecânico</label>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={filters.mechanicId ?? ''}
              onChange={(e) => updateFilter('mechanicId', e.target.value)}
            >
              <option value="">Todos</option>
              {(teamQuery.data?.team ?? []).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">De</label>
            <input
              type="date"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={filters.from ?? ''}
              onChange={(e) => updateFilter('from', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Até</label>
            <input
              type="date"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={filters.to ?? ''}
              onChange={(e) => updateFilter('to', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <SkeletonTable rows={8} />
      ) : isError && !data ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : orders.length === 0 ? (
        neverHadOS ? (
          <EmptyState
            icon={<ClipboardList size={28} />}
            title="Sua primeira OS começa aqui"
            description="Abra uma ordem de serviço para registrar o veículo, fotografar a entrada e acompanhar a execução até a entrega."
            action={canOpen ? { label: 'Abrir primeira OS', href: '/service-orders/new' } : undefined}
          />
        ) : (
          <EmptyState
            icon={<SearchX size={28} />}
            title="Nenhuma OS encontrada"
            description="Não encontramos ordens de serviço com os filtros selecionados. Tente ajustar os filtros ou crie uma nova OS."
            action={canOpen ? { label: 'Nova OS', href: '/service-orders/new' } : undefined}
            secondaryAction={{
              label: 'Limpar filtros',
              onClick: () => {
                setFilters({ page: 1, limit: 20 });
                setPlateInput('');
                setNumberInput('');
              },
            }}
          />
        )
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Placa</TableHead>
                  <TableHead>Veículo</TableHead>
                  <TableHead>Mecânico</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((os) => {
                  const statusCfg = STATUS_MAP[os.status];
                  const moduleStage = moduleStageText(os.module, os.stage, stageConfig);
                  return (
                    <TableRow
                      key={os.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedOsId(os.id)}
                    >
                      <TableCell className="font-mono font-semibold">{os.number}</TableCell>
                      <TableCell className="font-mono">{os.plate}</TableCell>
                      <TableCell>{os.vehicle}</TableCell>
                      <TableCell>{os.mechanicName}</TableCell>
                      <TableCell>
                        {statusCfg ? (
                          <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                        ) : (
                          <Badge variant="outline">{os.status}</Badge>
                        )}
                        {moduleStage && (
                          <p className="mt-1 text-xs text-muted-foreground">{moduleStage}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {os.deadline
                          ? new Date(os.deadline).toLocaleDateString('pt-BR')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {new Date(os.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Pagina {page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setFilters((prev) => ({ ...prev, page: page - 1 }))}
                >
                  <ChevronLeft size={14} />
                  Anterior
                </button>
                <button
                  className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
                  disabled={page >= totalPages}
                  onClick={() => setFilters((prev) => ({ ...prev, page: page + 1 }))}
                >
                  Proximo
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Side Panel */}
      <SidePanel
        open={!!selectedOsId}
        onClose={() => setSelectedOsId(null)}
        title="Resumo da OS"
      >
        {selectedOsId && (
          <OSSidePanelContent
            osId={selectedOsId}
            onNavigate={() => setSelectedOsId(null)}
          />
        )}
      </SidePanel>
    </motion.div>
  );
}
